import { gql } from '@apollo/client';
import gqlClient from '../../../../utils/apolloClient';
import {
  insertDishCategoryModifierAssignment,
  insertDishModifierAssignments,
  deleteDishCategoryModifierAssignment,
  insertDrinkCategoryModifierAssignment,
  insertDrinkModifierAssignments,
  deleteDrinkCategoryModifierAssignment,
  getDishesInCategory,
  getDrinksInCategory,
} from '../Common/components/StepModifiers/mutations';

const updateCategoryMutation = gql`
  mutation ($pk_columns: categories_pk_columns_input!, $set: categories_set_input!) {
    update_categories_by_pk(pk_columns: $pk_columns, _set: $set) {
      id
    }
  }
`;

const updateDishModifierMutation = gql`
  mutation ($id: uuid!, $set: dish_modifiers_set_input!) {
    update_dish_modifiers_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
    }
  }
`;

const updateDrinkModifierMutation = gql`
  mutation ($id: uuid!, $set: drink_modifiers_set_input!) {
    update_drink_modifiers_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
    }
  }
`;

export interface ModifierFields {
  name: string;
  price: number;
  tax: number;
}

/**
 * Persists an edit to an existing modifier's name/price/tax (BLUM-5927).
 * Modifiers are shared rows, so this updates the modifier everywhere it is used.
 */
export const updateModifier = async (categoryType: string, id: string, set: ModifierFields) => {
  const mutation = categoryType === 'dish' ? updateDishModifierMutation : updateDrinkModifierMutation;
  return gqlClient.mutate<{ update_dish_modifiers_by_pk?: { id: string }; update_drink_modifiers_by_pk?: { id: string } }>({ mutation, variables: { id, set } });
};

export const updateCategory = async (variables: object) => {
  return await gqlClient.mutate<{
    update_categories_by_pk: {
      id: string;
    };
  }>({ mutation: updateCategoryMutation!, variables });
};

const updateCategoryDrinksMutation = gql`
  mutation ($where: drinks_bool_exp!, $set: drinks_set_input!) {
    update_drinks(where: $where, _set: $set) {
      affected_rows
    }
  }
`;

export const updateCategoryDrinks = async (variables: { where: object; set: object }) => {
  return await gqlClient.mutate<{
    update_drinks: {
      affected_rows: number;
    };
  }>({
    mutation: updateCategoryDrinksMutation,
    variables,
  });
};

const updateCategoryDishesMutation = gql`
  mutation ($where: dishes_bool_exp!, $set: dishes_set_input!) {
    update_dishes(where: $where, _set: $set) {
      affected_rows
    }
  }
`;

export const updateCategoryDishes = async (variables: { where: object; set: object }) => {
  return await gqlClient.mutate<{
    update_dishes: {
      affected_rows: number;
    };
  }>({
    mutation: updateCategoryDishesMutation,
    variables,
  });
};

// Function to handle updating category modifiers and optionally applying them to category items
export const updateCategoryModifiers = async ({
  categoryId,
  categoryType,
  organisationId,
  modifiersToAdd,
  modifiersToRemove,
  applyToItems = false,
}: {
  categoryId: string;
  categoryType: string;
  organisationId: string;
  modifiersToAdd: any[];
  modifiersToRemove: string[];
  applyToItems?: boolean;
}) => {
  try {
    // Add new modifier assignments to the category
    for (const modifierData of modifiersToAdd) {
      if (categoryType === 'dish') {
        await insertDishCategoryModifierAssignment({ object: modifierData });
      } else if (categoryType === 'drink') {
        await insertDrinkCategoryModifierAssignment({ object: modifierData });
      }
    }

    // Remove modifier assignments from the category
    for (const assignmentId of modifiersToRemove) {
      if (categoryType === 'dish') {
        await deleteDishCategoryModifierAssignment({ id: assignmentId });
      } else if (categoryType === 'drink') {
        await deleteDrinkCategoryModifierAssignment({ id: assignmentId });
      }
    }

    // If applyToItems is true, apply the modifiers to all items in the category
    if (applyToItems) {
      // Get all items in the category
      let items: Array<{ id: string; organisation_id: string }> = [];

      if (categoryType === 'dish') {
        const response = await getDishesInCategory({ categoryId });
        items = response.data.dishes;
      } else if (categoryType === 'drink') {
        const response = await getDrinksInCategory({ categoryId });
        items = response.data.drinks;
      }

      if (items.length > 0) {
        // For modifiers to add
        if (modifiersToAdd.length > 0) {
          // Create modifier assignments for each item, but check for existing assignments first
          const modifierAssignments = [];
          const modifierIdsToAdd = modifiersToAdd.map((m: any) => m.modifier_id);

          for (const item of items) {
            // Query existing modifier assignments for this item to prevent duplicates
            const getExistingAssignmentsQuery = gql`
              query GetExistingItemModifierAssignments($itemId: uuid!, $modifierIds: [uuid!]) {
                ${categoryType === 'dish' ? 'dish_modifier_assignments' : 'drink_modifier_assignments'}(
                  where: {
                    ${categoryType === 'dish' ? 'dish_id' : 'drink_id'}: { _eq: $itemId },
                    modifier_id: { _in: $modifierIds }
                  }
                ) {
                  id
                  modifier_id
                }
              }
            `;
            
            const existingAssignmentsResponse = await gqlClient.query({
              query: getExistingAssignmentsQuery,
              variables: { 
                itemId: item.id, 
                modifierIds: modifierIdsToAdd 
              },
              fetchPolicy: 'no-cache',
            });
            
            const existingAssignments = categoryType === 'dish' 
              ? existingAssignmentsResponse.data.dish_modifier_assignments 
              : existingAssignmentsResponse.data.drink_modifier_assignments;
            
            const existingModifierIds = existingAssignments.map((a: any) => a.modifier_id);

            for (const modifierData of modifiersToAdd) {
              // Only add if the item doesn't already have this modifier
              if (!existingModifierIds.includes(modifierData.modifier_id)) {
                modifierAssignments.push({
                  [categoryType === 'dish' ? 'dish_id' : 'drink_id']: item.id,
                  modifier_id: modifierData.modifier_id,
                  organisation_id: organisationId,
                });
              }
            }
          }

          // Insert the modifier assignments (only non-duplicates)
          if (modifierAssignments.length > 0) {
            if (categoryType === 'dish') {
              await insertDishModifierAssignments({ objects: modifierAssignments });
            } else if (categoryType === 'drink') {
              await insertDrinkModifierAssignments({ objects: modifierAssignments });
            }
          }
        }

        // For modifiers to remove
        if (modifiersToRemove.length > 0) {
          // First, get the modifier IDs associated with the assignment IDs that are being removed
          const getCategoryAssignmentsMutation = gql`
            query GetCategoryAssignments($ids: [uuid!]) {
              ${categoryType === 'dish' ? 'dish_category_modifier_assignments' : 'drink_category_modifier_assignments'}(
                where: { id: { _in: $ids } }
              ) {
                id
                modifier_id
              }
            }
          `;
          
          const categoryAssignmentsResponse = await gqlClient.query({
            query: getCategoryAssignmentsMutation,
            variables: { ids: modifiersToRemove },
            fetchPolicy: 'no-cache',
          });
          
          const categoryAssignments = categoryType === 'dish' 
            ? categoryAssignmentsResponse.data.dish_category_modifier_assignments 
            : categoryAssignmentsResponse.data.drink_category_modifier_assignments;
          
          const modifierIdsToRemove = categoryAssignments.map((assignment: any) => assignment.modifier_id);
          
          // For each item in the category, remove the modifier assignments for the removed modifiers
          for (const item of items) {
            if (modifierIdsToRemove.length > 0) {
              // Get all modifier assignments for this item that match the modifier IDs to remove
              const getItemModifierAssignmentsQuery = gql`
                query GetItemModifierAssignments($itemId: uuid!, $modifierIds: [uuid!]) {
                  ${categoryType === 'dish' ? 'dish_modifier_assignments' : 'drink_modifier_assignments'}(
                    where: {
                      ${categoryType === 'dish' ? 'dish_id' : 'drink_id'}: { _eq: $itemId },
                      modifier_id: { _in: $modifierIds }
                    }
                  ) {
                    id
                  }
                }
              `;
              
              const itemModifierAssignmentsResponse = await gqlClient.query({
                query: getItemModifierAssignmentsQuery,
                variables: { 
                  itemId: item.id, 
                  modifierIds: modifierIdsToRemove 
                },
                fetchPolicy: 'no-cache',
              });
              
              const itemModifierAssignments = categoryType === 'dish' 
                ? itemModifierAssignmentsResponse.data.dish_modifier_assignments 
                : itemModifierAssignmentsResponse.data.drink_modifier_assignments;
              
              // Delete each assignment
              for (const assignment of itemModifierAssignments) {
                const deleteItemModifierAssignmentMutation = gql`
                  mutation DeleteItemModifierAssignment($id: uuid!) {
                    delete_${categoryType}_modifier_assignments_by_pk(id: $id) {
                      id
                    }
                  }
                `;
                
                await gqlClient.mutate({
                  mutation: deleteItemModifierAssignmentMutation,
                  variables: { id: assignment.id },
                });
              }
            }
          }
        }
      }
    }

    return true;
  } catch (error) {
    console.error('Error updating category modifiers:', error);
    throw error;
  }
};
